"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import {
  PROGRAMME_BATCH_SIZE,
  filterProgramme,
  getProgrammeFacets,
  getVisibleProgramme,
  type ProgrammeFacets,
  type ProgrammeFilters,
} from "@/lib/museum/programme";
import type { ProgrammeEntry } from "@/lib/museum/types";
import { PlaceCartography } from "@/app/_components/museum/PlaceHistoryScene";

import styles from "./collection-hall.module.css";

const WING_OPTIONS = [
  { value: "all", label: "All exhibitions" },
  { value: "sports", label: "Sports" },
  { value: "places", label: "Places" },
] as const;

const URL_FILTER_KEYS = ["q", "wing", "country", "region", "family", "environment"] as const;

export type MuseumCollectionState = {
  filters: ProgrammeFilters;
  visibleCount: number;
};

type EditableFilterKey = Exclude<keyof ProgrammeFilters, "wing">;

export type MuseumCollectionAction =
  | { type: "hydrate"; search: string; entries: ProgrammeEntry[] }
  | { type: "update-filter"; key: EditableFilterKey; value: string }
  | { type: "choose-wing"; wing: ProgrammeFilters["wing"] }
  | { type: "show-more" }
  | { type: "clear" };

export type FacetVisibility = {
  country: boolean;
  region: boolean;
  sportFamily: boolean;
  environment: boolean;
  any: boolean;
};

function hasDistinctFacetResults(
  entries: ProgrammeEntry[],
  options: string[],
  getValues: (entry: ProgrammeEntry) => string[],
): boolean {
  if (options.length < 2) return false;

  const resultSignatures = options.map((option) => entries
    .filter((entry) => getValues(entry).includes(option))
    .map((entry) => entry.slug)
    .sort((left, right) => left.localeCompare(right, "en-GB"))
    .join("\u001f"));

  return new Set(resultSignatures).size >= 2;
}

function parseCollectionFilters(search: string): ProgrammeFilters {
  const params = new URLSearchParams(search);
  const wing = params.get("wing");

  return {
    query: params.get("q") ?? "",
    wing: wing === "sports" || wing === "places" ? wing : "all",
    country: params.get("country") ?? "",
    region: params.get("region") ?? "",
    sportFamily: params.get("family") ?? "",
    environment: params.get("environment") ?? "",
  };
}

export function createCollectionState(
  search: string,
  entries: ProgrammeEntry[] = [],
): MuseumCollectionState {
  return {
    filters: sanitiseCollectionFilters(search, entries),
    visibleCount: PROGRAMME_BATCH_SIZE,
  };
}

export function serializeCollectionSearch(
  filters: ProgrammeFilters,
  currentSearch: string,
): string {
  const params = new URLSearchParams(currentSearch);

  URL_FILTER_KEYS.forEach((key) => params.delete(key));
  if (filters.query) params.set("q", filters.query);
  if (filters.wing !== "all") params.set("wing", filters.wing);
  if (filters.country) params.set("country", filters.country);
  if (filters.region) params.set("region", filters.region);
  if (filters.sportFamily) params.set("family", filters.sportFamily);
  if (filters.environment) params.set("environment", filters.environment);

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function museumCollectionReducer(
  state: MuseumCollectionState,
  action: MuseumCollectionAction,
): MuseumCollectionState {
  switch (action.type) {
    case "hydrate":
      return createCollectionState(action.search, action.entries);
    case "update-filter":
      return {
        filters: { ...state.filters, [action.key]: action.value },
        visibleCount: PROGRAMME_BATCH_SIZE,
      };
    case "choose-wing":
      return {
        filters: {
          ...state.filters,
          wing: action.wing,
          ...(action.wing === "sports" ? { country: "", region: "" } : {}),
          ...(action.wing === "places" ? { sportFamily: "", environment: "" } : {}),
        },
        visibleCount: PROGRAMME_BATCH_SIZE,
      };
    case "show-more":
      return { ...state, visibleCount: state.visibleCount + PROGRAMME_BATCH_SIZE };
    case "clear":
      return createCollectionState("");
  }
}

export function getFacetVisibility(
  facets: ProgrammeFacets,
  wing: ProgrammeFilters["wing"],
  entries: ProgrammeEntry[],
): FacetVisibility {
  const includePlaces = wing === "all" || wing === "places";
  const includeSports = wing === "all" || wing === "sports";
  const eligibleEntries = entries.filter((entry) => wing === "all" || entry.wing === wing);
  const visibility = {
    country: includePlaces && hasDistinctFacetResults(
      eligibleEntries,
      facets.countries,
      (entry) => entry.programme.place ? [entry.programme.place.country] : [],
    ),
    region: includePlaces && hasDistinctFacetResults(
      eligibleEntries,
      facets.regions,
      (entry) => entry.programme.place ? [entry.programme.place.region] : [],
    ),
    sportFamily: includeSports && hasDistinctFacetResults(
      eligibleEntries,
      facets.sportFamilies,
      (entry) => entry.programme.sport ? [entry.programme.sport.family] : [],
    ),
    environment: includeSports && hasDistinctFacetResults(
      eligibleEntries,
      facets.environments,
      (entry) => entry.programme.sport?.environments ?? [],
    ),
  };

  return {
    ...visibility,
    any: Object.values(visibility).some(Boolean),
  };
}

function keepAvailableFacet(
  value: string,
  options: string[],
  visible: boolean,
): string {
  return visible && options.includes(value) ? value : "";
}

export function sanitiseCollectionFilters(
  search: string,
  entries: ProgrammeEntry[],
): ProgrammeFilters {
  const filters = parseCollectionFilters(search);
  const facets = getProgrammeFacets(entries);
  const visibility = getFacetVisibility(facets, filters.wing, entries);

  return {
    ...filters,
    country: keepAvailableFacet(filters.country, facets.countries, visibility.country),
    region: keepAvailableFacet(filters.region, facets.regions, visibility.region),
    sportFamily: keepAvailableFacet(
      filters.sportFamily,
      facets.sportFamilies,
      visibility.sportFamily,
    ),
    environment: keepAvailableFacet(
      filters.environment,
      facets.environments,
      visibility.environment,
    ),
  };
}

function writeFiltersToUrl(filters: ProgrammeFilters): void {
  const search = serializeCollectionSearch(filters, window.location.search);
  const nextUrl = `${window.location.pathname}${search}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

function CompleteIndex({
  entries,
  markEntries = false,
}: {
  entries: ProgrammeEntry[];
  markEntries?: boolean;
}) {
  const alphabeticalEntries = [...entries].sort((left, right) => (
    left.title.localeCompare(right.title, "en-GB")
  ));

  return (
    <section className={styles.completeIndex} aria-labelledby={markEntries ? "complete-index-title" : undefined}>
      <p className={styles.utilityLabel}>Complete catalogue</p>
      <h2 id={markEntries ? "complete-index-title" : undefined}>All exhibitions</h2>
      <ul>
        {alphabeticalEntries.map((entry) => (
          <li key={entry.slug} {...(markEntries ? { "data-complete-index-entry": entry.slug } : {})}>
            <Link href={`/museum/${entry.slug}`} prefetch={false}>
              <span>{entry.title}</span>
              <span>{entry.wing === "places" ? "Place" : "Sport"}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Facet({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: string[];
  value: string;
}) {
  return (
    <label className={styles.facet}>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function Poster({ entry, index }: { entry: ProgrammeEntry; index: number }) {
  const featured = entry.programme.featuredRank !== null;
  const wingLabel = entry.wing === "places" ? "Place" : "Sport";
  const sizes = featured
    ? "(max-width: 767px) calc(100vw - 2.5rem), (max-width: 1759px) calc(50vw - 3rem), 52rem"
    : "(max-width: 767px) calc(100vw - 2.5rem), (max-width: 1023px) calc(50vw - 2.75rem), (max-width: 1759px) calc(33.333vw - 3rem), 34rem";
  const imageStyle = { objectPosition: entry.programme.image.focalPoint } satisfies CSSProperties;
  const placeArt = entry.programme.place?.coordinates
    && entry.programme.place.instrument
    && entry.programme.place.seed !== undefined
    ? {
        coordinates: entry.programme.place.coordinates,
        instrument: entry.programme.place.instrument,
        seed: entry.programme.place.seed,
      }
    : null;

  return (
    <li
      className={`${styles.poster} ${featured ? styles.posterFeatured : styles.posterRegular}`}
      data-museum-poster={entry.slug}
      data-featured={featured ? "true" : undefined}
      data-tone={entry.programme.image.tone}
    >
      <Link
        href={`/museum/${entry.slug}`}
        prefetch={false}
        className={styles.posterLink}
      >
        {placeArt ? (
          <span className={styles.posterMapArt} aria-hidden="true">
            <PlaceCartography {...placeArt} />
          </span>
        ) : (
          <Image
            className={styles.posterImage}
            src={entry.programme.image.src}
            width={entry.programme.image.width}
            height={entry.programme.image.height}
            sizes={sizes}
            priority={index === 0}
            loading={index === 0 ? "eager" : "lazy"}
            alt=""
            style={imageStyle}
          />
        )}
        <span className={styles.posterVeil} aria-hidden="true" />
        <span className={styles.posterContent}>
          <span className={styles.posterCatalogue}>
            <span>{wingLabel} wing</span>
            <span>AOD / {String(entry.programme.displayOrder).padStart(3, "0")}</span>
          </span>
          <span className={styles.posterCopy}>
            <span className={styles.posterTitle}>{entry.title}</span>
            <span className={styles.posterSubtitle}>{entry.subtitle}</span>
          </span>
          <span className={styles.posterFoot}>
            <span>
              <span className={styles.posterDuration}>{entry.durationMin} min</span>
              <span className={styles.posterJourney}>{entry.programme.journeyLabel}</span>
            </span>
            <span className={styles.enterCue}>Enter exhibition <span aria-hidden="true">↗</span></span>
          </span>
        </span>
      </Link>
    </li>
  );
}

export function MuseumCollection({ entries }: { entries: ProgrammeEntry[] }) {
  const [{ filters, visibleCount }, dispatch] = useReducer(
    museumCollectionReducer,
    "",
    (search) => createCollectionState(search, entries),
  );
  const [announcedCount, setAnnouncedCount] = useState(entries.length);
  const skipInitialUrlWrite = useRef(true);

  const facets = useMemo(() => getProgrammeFacets(entries), [entries]);
  const facetVisibility = useMemo(
    () => getFacetVisibility(facets, filters.wing, entries),
    [entries, facets, filters.wing],
  );
  const filteredEntries = useMemo(() => filterProgramme(entries, filters), [entries, filters]);
  const { visible, remaining } = useMemo(
    () => getVisibleProgramme(filteredEntries, visibleCount),
    [filteredEntries, visibleCount],
  );

  useEffect(() => {
    const restoreUrlFilters = () => {
      dispatch({ type: "hydrate", search: window.location.search, entries });
    };

    restoreUrlFilters();
    window.addEventListener("popstate", restoreUrlFilters);
    return () => window.removeEventListener("popstate", restoreUrlFilters);
  }, [entries]);

  useEffect(() => {
    if (skipInitialUrlWrite.current) {
      skipInitialUrlWrite.current = false;
      return;
    }
    writeFiltersToUrl(filters);
  }, [filters]);

  useEffect(() => {
    const timer = window.setTimeout(() => setAnnouncedCount(filteredEntries.length), 250);
    return () => window.clearTimeout(timer);
  }, [filteredEntries.length, filters]);

  const resultsLabel = filters.wing === "all"
    ? "Sports and Places exhibition programme"
    : `${filters.wing === "sports" ? "Sports" : "Places"} exhibition programme`;

  let programmeContent: ReactNode;
  if (entries.length === 0) {
    programmeContent = (
      <div className={styles.emptyState}>
        <p>The first exhibitions are being curated.</p>
        <span>Return soon to enter the Archive of Days.</span>
      </div>
    );
  } else if (filteredEntries.length === 0) {
    programmeContent = (
      <div className={styles.emptyState}>
        <p>No exhibitions match that search yet.</p>
        <span>Try another search or return to the complete collection.</span>
        <button type="button" onClick={() => dispatch({ type: "clear" })}>Clear filters</button>
      </div>
    );
  } else {
    programmeContent = (
      <section className={styles.resultsRegion} aria-labelledby="museum-results-title">
        <h3 className={styles.srOnly} id="museum-results-title">{resultsLabel}</h3>
        <ul className={styles.programmeWall} data-museum-programme>
          {visible.map((entry, index) => <Poster key={entry.slug} entry={entry} index={index} />)}
        </ul>
        {remaining > 0 ? (
          <div className={styles.reveal}>
            <button
              type="button"
              onClick={() => dispatch({ type: "show-more" })}
            >
              <span>Show more exhibitions</span>
              <span>{remaining} remaining</span>
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className={styles.collection} aria-labelledby="museum-collection-title">
        <header className={styles.collectionHeader}>
          <p className={styles.utilityLabel}>The collection</p>
          <h2 id="museum-collection-title">Choose a history. Follow what changed.</h2>
          <p className={styles.resultCount} aria-live="polite" aria-atomic="true">
            <span>{announcedCount}</span> {announcedCount === 1 ? "exhibition" : "exhibitions"}
          </p>
        </header>

        <div className={styles.controls} aria-label="Museum catalogue controls">
          <label className={styles.searchField}>
            <span>Search the museum</span>
            <span className={styles.searchInputWrap}>
              <input
                type="search"
                aria-label="Search the museum"
                value={filters.query}
                onChange={(event) => dispatch({
                  type: "update-filter",
                  key: "query",
                  value: event.target.value,
                })}
                placeholder="Sport, place or landscape"
              />
              <span aria-hidden="true">⌕</span>
            </span>
        </label>

          <div className={styles.wingFilters} role="group" aria-label="Filter by collection wing">
            {WING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={filters.wing === option.value}
                onClick={() => dispatch({ type: "choose-wing", wing: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>

          {facetVisibility.any ? (
            <div className={styles.facets} aria-label="Refine the collection">
              {facetVisibility.country ? (
                <Facet
                  label="Country"
                  options={facets.countries}
                  value={filters.country}
                  onChange={(country) => dispatch({
                    type: "update-filter",
                    key: "country",
                    value: country,
                  })}
                />
              ) : null}
              {facetVisibility.region ? (
                <Facet
                  label="Region"
                  options={facets.regions}
                  value={filters.region}
                  onChange={(region) => dispatch({
                    type: "update-filter",
                    key: "region",
                    value: region,
                  })}
                />
              ) : null}
              {facetVisibility.sportFamily ? (
                <Facet
                  label="Sport family"
                  options={facets.sportFamilies}
                  value={filters.sportFamily}
                  onChange={(sportFamily) => dispatch({
                    type: "update-filter",
                    key: "sportFamily",
                    value: sportFamily,
                  })}
                />
              ) : null}
              {facetVisibility.environment ? (
                <Facet
                  label="Environment"
                  options={facets.environments}
                  value={filters.environment}
                  onChange={(environment) => dispatch({
                    type: "update-filter",
                    key: "environment",
                    value: environment,
                  })}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {programmeContent}

        {entries.length > 0 ? (
          <footer className={styles.collectionNote}>
            <span aria-hidden="true">AOD</span>
            <p><strong>The collection is growing.</strong> New histories of sport and place are being researched and curated.</p>
          </footer>
        ) : null}

        <div className={styles.printIndex} aria-hidden="true">
          <CompleteIndex entries={entries} />
        </div>
        <noscript><CompleteIndex entries={entries} markEntries /></noscript>
    </section>
  );
}
